import { useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { IVehicle } from "../../models/Vehicle";
import useJobMutations from "../../data/useJobMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { IJob } from "../../models/Job";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import Card from "../shared/Card/Card";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import openInNewTab from "../../utils/openInNewTab";
import useJobsByVehicle from "../../data/useJobsByVehicle";
import LoadingWheel from "../shared/LoadingWheel";



export default function VehicleJobs({ vehicle }: { vehicle: IVehicle }) {
  const queryClient = useQueryClient();
  const { jobs, remove } = useJobsByVehicle(vehicle.id);
  const { deleteHandlers } = useDefaultCRUDHandlers("Job");
  const [deleteJob, setDeleteJob] = useState<IJob | null>(null);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);

  const handleDeleteJob = () => {
    setDeleteLoading(true);
    if (deleteJob) {
      remove.mutate(deleteJob.id, {
        onSuccess: () => {
          queryClient.invalidateQueries(["vehicles", vehicle.id]);
          setDeleteOpen(false);
          deleteHandlers.onSuccess();
        },
        onError: deleteHandlers.onError,
        onSettled: () => {
          setDeleteLoading(false);
        },
      });
    } else {
      deleteHandlers.onError();
      setDeleteLoading(false);
    }
  };

  return (
    <>
      <ConfirmModal
        open={deleteOpen}
        setOpen={setDeleteOpen}
        title={"Delete job?"}
        message={`Are you sure you want to remove this job?\n It will no longer appear on the job calendar.`}
        confirmButtonText={"Delete"}
        onConfirm={handleDeleteJob}
        isLoading={deleteLoading}
      />
      <Card title="Vehicle Jobs" bodyClassName="p-4" collapsible>
        <div className="flex flex-col justify-evenly gap-y-2">
          {jobs.isLoading ? <LoadingWheel /> : jobs?.data?.data.map((job) => (
            <div
              key={job.id}
              className="flex justify-between border-b-2 last:border-b-0 pb-2"
            >
              <div className="flex flex-col gap-y-2">
                <h1 className="text-lg font-semibold">
                  {job.title.replace(/-[^-]*$/, "")} - {job.duration} mins
                </h1>
                <p>{job.description}</p>
              </div>
              <div className="flex gap-x-2 items-center">
                <ButtonInput
                  label={"View"}
                  isSubmit={false}
                  onClick={() => openInNewTab(`/jobs/${job.id}`)}
                  classes={""}
                />
                <ButtonInput
                  label={"Delete Job"}
                  isSubmit={false}
                  onClick={() => {
                    setDeleteOpen(true);
                    setDeleteJob(job);
                  }}
                  classes={"!bg-red-500 hover:!bg-red-700"}
                />
              </div>
            </div>
          ))}
        </div>
      </Card>
    </>
  );
};